import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Tooltip,
} from '@mui/material'
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  MoreVert,
  Place,
  Download,
  ChevronRight,
  ChevronLeft,
  SlowMotionVideo,
} from '@mui/icons-material'
import { useState } from 'react'
import { ConvertTime } from '@/components/smartUtils/conversion/ConvertTime'
import { preview } from '@/components/form/preview'
import { useEnvironmentURLs } from '@/atoms'
import { togglePoDMarker, fitMap, useActivePodMarker } from '@/map'

import { useTexts } from '../useTexts'
import { useAudioPlayer } from './hooks/useAudioPlayer'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]

type MenuView = 'main' | 'speed'

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

interface AudioPlayerProps {
  src: string
  index: number
  time: React.ReactNode
  text?: string
  uuid?: string
  latLng?: uui.domain.LatLng | null
}

function AudioPlayer({ src, index, time, text, uuid, latLng }: AudioPlayerProps) {
  const texts = useTexts()
  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    bufferedPct,
    playbackRate,
    togglePlay,
    handleSeek,
    handleSeekCommitted,
    toggleMute,
    handleVolumeChange,
    setPlaybackRate,
  } = useAudioPlayer()

  const [showVolume, setShowVolume] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuView, setMenuView] = useState<MenuView>('main')

  const open = Boolean(anchorEl)
  const activePodMarker = useActivePodMarker()
  const isOnMap = !!uuid && activePodMarker?.uid === uuid

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuView('main')
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuView('main')
  }

  const handleShowOnMap = () => {
    if (latLng && uuid) {
      togglePoDMarker(uuid, latLng)

      // Only fit when revealing the marker; toggling it off shouldn't recenter the map.
      if (!isOnMap) {
        fitMap([latLng], { minResolution: 3, preventIfVisible: true }, 'clear')
      }
    }
    handleMenuClose()
  }

  const handleDownload = async () => {
    handleMenuClose()
    if (!src) return

    // The pods host is a different origin, so the anchor `download` attribute is ignored
    // and the file just opens in a tab. Fetch the bytes and download the same-origin blob
    // instead, so the browser actually saves the file.
    try {
      const res = await fetch(src, { credentials: 'include' })
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `voice_memo_${index + 1}.aac`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    } catch {
      // Fall back to opening the file if the fetch is blocked.
      window.open(src, '_blank', 'noopener,noreferrer')
    }
  }

  const handleSelectSpeed = (speed: number) => {
    setPlaybackRate(speed)
    handleMenuClose()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <audio ref={audioRef} src={src} preload="auto" />
        <IconButton
          onClick={togglePlay}
          size="small"
          sx={{
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
        </IconButton>
        <Typography variant="caption" sx={{ minWidth: 40, color: 'text.secondary' }}>
          {formatTime(currentTime)}
        </Typography>
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          {/* Behind the slider: light track = unloaded, medium fill = loaded/buffered.
              The slider's own (dark) track on top shows the played portion. */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: 4,
              borderRadius: 2,
              bgcolor: 'action.hover',
              overflow: 'hidden',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ width: `${bufferedPct}%`, height: '100%', bgcolor: 'action.disabled' }} />
          </Box>
          <Slider
            size="small"
            value={duration > 0 ? currentTime : 0}
            min={0}
            max={duration || 0}
            onChange={handleSeek}
            onChangeCommitted={handleSeekCommitted}
            aria-label={texts.voiceMemo.label}
            sx={{
              flex: 1,
              color: 'text.primary',
              // Rail transparent so the buffered layer behind shows through.
              '& .MuiSlider-rail': { opacity: 0 },
              '& .MuiSlider-track': { height: 4, border: 'none' },
              // Flat, thumb-less bar by default; reveal the thumb on hover/drag for scrubbing.
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                opacity: 0,
                transition: 'opacity 0.2s ease',
              },
              '&:hover .MuiSlider-thumb, & .MuiSlider-thumb.Mui-active': {
                opacity: 1,
              },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 40, color: 'text.secondary' }}>
          {formatTime(duration)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: showVolume ? 1.5 : 0,
            borderRadius: 999,
            // Light pill highlighting the whole group (slider + icon) on hover.
            bgcolor: showVolume ? 'action.hover' : 'transparent',
            transition: 'background-color 0.2s ease, padding 0.2s ease',
          }}
          onMouseEnter={() => setShowVolume(true)}
          onMouseLeave={() => setShowVolume(false)}
        >
          <Slider
            size="small"
            value={isMuted ? 0 : volume}
            min={0}
            max={1}
            step={0.01}
            onChange={handleVolumeChange}
            aria-label={texts.voiceMemo.turnOffVolume}
            sx={{
              width: showVolume ? 72 : 0,
              opacity: showVolume ? 1 : 0,
              transition: 'width 0.2s ease, opacity 0.2s ease',
              visibility: showVolume ? 'visible' : 'hidden',
              // Match the dark playback bar rather than the default blue primary.
              color: 'text.primary',
            }}
          />
          <Tooltip title={isMuted ? texts.voiceMemo.turnOnVolume : texts.voiceMemo.turnOffVolume}>
            <IconButton
              onClick={toggleMute}
              size="small"
              sx={{
                // Slightly darker circle behind the icon while the group is hovered.
                bgcolor: showVolume ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <IconButton onClick={handleMenuClick} size="small" data-testid="voice-memo-menu">
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuView === 'main'
            ? [
                <MenuItem key="show-on-map" onClick={handleShowOnMap} disabled={!latLng}>
                  <ListItemIcon>
                    <Place fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {isOnMap ? texts.hideOnMap.label : texts.showOnMap.label}
                  </ListItemText>
                </MenuItem>,
                <MenuItem key="download" onClick={handleDownload}>
                  <ListItemIcon>
                    <Download fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{texts.download.label}</ListItemText>
                </MenuItem>,
                <MenuItem key="playback-speed" onClick={() => setMenuView('speed')}>
                  <ListItemIcon>
                    <SlowMotionVideo fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{texts.voiceMemo.playbackSpeed}</ListItemText>
                  <ChevronRight fontSize="small" sx={{ color: 'action.active', ml: 2 }} />
                </MenuItem>,
              ]
            : [
                <MenuItem key="back" onClick={() => setMenuView('main')}>
                  <ListItemIcon>
                    <ChevronLeft fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{texts.voiceMemo.playbackSpeed}</ListItemText>
                </MenuItem>,
                ...PLAYBACK_SPEEDS.map(speed => (
                  <MenuItem
                    key={speed}
                    selected={playbackRate === speed}
                    onClick={() => handleSelectSpeed(speed)}
                  >
                    <ListItemText inset>
                      {speed === 1 ? texts.voiceMemo.playbackSpeedNormal : `${speed}x`}
                    </ListItemText>
                  </MenuItem>
                )),
              ]}
        </Menu>
      </Box>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        <Typography variant="caption">
          {text ? `${text} • ` : ''}
          {time}
        </Typography>
      </Box>
    </Box>
  )
}

export function VoiceMemo(props: Props) {
  const { extOrder } = props
  const texts = useTexts()
  const { podsBaseUrl } = useEnvironmentURLs()

  const audios = extOrder.orderStep.trackingData?.pods?.audios
  const audiosList = audios ? Object.values(audios) : []

  if (audiosList.length === 0) {
    return (
      <preview.Field label={texts.voiceMemo.label} testId="execution-voice-memo">
        <preview.NullValue />
      </preview.Field>
    )
  }

  return (
    <Box data-testid="execution-voice-memo">
      <preview.Label>{texts.voiceMemo.label}</preview.Label>
      {audiosList.map((audio, idx) => {
        const { sec, text, uuid, latLng } = audio
        const time = <ConvertTime seconds={sec} showSeconds={false} />
        const src = audio.podType === 'audios' ? `${podsBaseUrl}${audio.token}` : ''

        return (
          <Box
            key={uuid || `audio_${idx}`}
            sx={{ mb: 2 }}
            data-testid={`execution-voice-memo-item-${idx}`}
          >
            <AudioPlayer
              src={src}
              index={idx}
              time={time}
              text={text}
              uuid={uuid}
              latLng={latLng}
            />
          </Box>
        )
      })}
    </Box>
  )
}
